import { useSuspenseQuery } from "@apollo/client";
import { DataTable, PrimaryFullScreenSpinner } from "@repo/ui";
import { brandsByKey } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { RiBankLine } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/providers")({
  head: () => opsHead("Providers"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const providersQueryDocument = graphql(`
  query AdminProviders {
    providers {
      id
      name
      internalName
    }
  }
`);

function View() {
  const { data } = useSuspenseQuery(providersQueryDocument);
  const providers = (data.providers ?? [])
    .slice()
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

  return (
    <AdminPageContainer testId="admin-providers">
      <AdminPageHeader
        title="Providers"
        subtitle="These are the providers in Primary's codebase, with which products are associated."
      />
      <DataTable
        data={providers}
        columns={columns}
        className="overflow-hidden border"
      />
    </AdminPageContainer>
  );
}

type ProviderRow = {
  id: string;
  name: string;
  internalName: string | null;
};

const columns: ColumnDef<ProviderRow>[] = [
  {
    accessorKey: "name",
    header: "Name",
  },
  {
    accessorKey: "displayName",
    header: "Display Name",
    cell: ({ row }) => {
      const bankDisplay = brandsByKey[row.original.name];
      return bankDisplay?.displayName ?? row.original.name;
    },
  },
  {
    accessorKey: "hasLogo",
    header: "Logo",
    cell: () => (
      <div className="ml-1 size-8 rounded-md bg-primary p-1.5">
        <RiBankLine className="size-full text-tertiary" />
      </div>
    ),
  },
  {
    accessorKey: "internalName",
    header: "Internal name",
    cell: ({ row }) => row.original.internalName ?? "No internal name.",
  },
  {
    accessorKey: "hasInternalLogo",
    header: "Internal logo",
    cell: () => (
      <div className="ml-1 size-8 rounded-md bg-primary p-1.5">
        <RiBankLine className="size-full text-tertiary" />
      </div>
    ),
  },
  {
    accessorKey: "id",
    header: "Provider ID",
  },
];
